import { ReactNode, AriaAttributes, useState, useRef, useId, useEffect, isValidElement, createContext, useContext, Children } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import { ChevronDownIcon } from "@icons";
import { focusNext } from "./accessibility";

const NOBREAK_SPACE = `\u00A0`;

interface DropdownSelectContextValue {
    selectedValue: unknown;
    onSelect: (value: unknown) => void;
    closeSelect: () => void;
}

const DropdownSelectContext = createContext<DropdownSelectContextValue | null>(null);

interface DropdownOptionProps<T extends string | number> {
    value: T;
    children: ReactNode;
}

export const DropdownOption = <T extends string | number>({ value, children }: DropdownOptionProps<T>) => {
    const context = useContext(DropdownSelectContext);
    if (!context) throw new Error(`DropdownOption must be used within DropdownSelect`);

    const isSelected = context.selectedValue === value;

    const handleSelect = () => {
        context.onSelect(value);
        context.closeSelect();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === `Enter` || e.key === ` `) {
            e.preventDefault();
            handleSelect();
        }
    };

    return (
        <div
            role="option"
            aria-selected={isSelected}
            tabIndex={0}
            onClick={handleSelect}
            onKeyDown={handleKeyDown}
            className={`w-full p-2 rounded cursor-pointer focus:outline-none focus-visible:outline-4 focus-visible:outline-blue-400 text-base leading-6 text-neutral-700 shrink-0 truncate ${
                isSelected
                    ? `bg-neutral-50`
                    : `hover:bg-neutral-50 focus:bg-neutral-50`
            }`}
        >
            {children}
        </div>
    );
};

export type DropdownSelectTriggerProps = {
    value: string | undefined;
    isOpen: boolean;
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    [`aria-expanded`]: AriaAttributes[`aria-expanded`];
    [`aria-haspopup`]: AriaAttributes[`aria-haspopup`];
    [`aria-controls`]: AriaAttributes[`aria-controls`];
};

export const DropdownSelectCompactTrigger = ({
    value,
    isOpen = false,
    onClick,
    ...aria
}: DropdownSelectTriggerProps) => (
    <button
        type="button"
        className={`bg-white border ${isOpen ? `border-neutral-300` : `border-neutral-200 hover:border-neutral-300`} rounded-lg pl-2.5 pr-2 py-1 focus-visible:outline-focus-ring flex items-center gap-1.5`}
        onClick={onClick}
        {...aria}
    >
        <span className="font-medium text-sm text-neutral-900 tracking-[0.28px] max-w-[112px] truncate">
            {value ?? `Select…`}
        </span>
        <ChevronDownIcon
            className="h-4 w-4 text-neutral-900 transition-transform duration-200"
            style={{ transform: isOpen ? `rotate(180deg)` : `rotate(0deg)` }}
        />
    </button>
);

export type DropdownSelectLabeledTriggerProps = DropdownSelectTriggerProps & {
    label: string;
    disabled?: boolean;
    id?: string;
};

export const DropdownSelectLabeledTrigger = ({
    label,
    value,
    isOpen = false,
    onClick,
    disabled,
    id,
    ...aria
}: DropdownSelectLabeledTriggerProps) => (
    <button
        type="button"
        id={id}
        disabled={disabled}
        className={`w-full border ${disabled ? `border-neutral-200` : isOpen ? `border-neutral-300` : `border-neutral-200 hover:border-neutral-300`} rounded-lg px-4 py-2 focus-visible:outline-focus-ring flex items-center justify-between gap-2 text-left ${disabled ? `bg-neutral-50` : `bg-white`}`}
        onClick={onClick}
        {...aria}
    >
        <div className="flex flex-col flex-1 min-w-0">
            <span className={`text-xs leading-4 font-normal tracking-[0.12px] ${disabled ? `text-neutral-500` : `text-neutral-700`}`}>
                {label}
            </span>
            <span className={`text-base leading-6 font-normal tracking-[0.16px] truncate ${disabled ? `text-neutral-500` : `text-neutral-950`}`}>
                {value ?? NOBREAK_SPACE}
            </span>
        </div>
        <ChevronDownIcon
            className="w-5 h-5 text-neutral-500 flex-shrink-0 transition-transform duration-200"
            style={{ transform: isOpen ? `rotate(180deg)` : `rotate(0deg)` }}
        />
    </button>
);

// Scans children for the DropdownOption whose value matches and returns its
// text label (children), used to display the current selection in the trigger.
const getLabelForValue = (children: ReactNode, value: unknown): string | undefined => {
    if (value === undefined) return undefined;
    let label: string | undefined;
    Children.forEach(children, (child) => {
        if (isValidElement(child) && (child.props as DropdownOptionProps<string | number>).value === value) {
            const optionChildren = (child.props as DropdownOptionProps<string | number>).children;
            if (typeof optionChildren === `string`) label = optionChildren;
        }
    });
    return label;
};

type TriggerProp = (props: DropdownSelectTriggerProps) => ReactNode;

interface DropdownSelectProps<T extends string | number> {
    children: ReactNode;
    /**
     * A trigger component that receives value/isOpen/onClick/aria-* as props.
     *
     * Simple: `trigger={DropdownSelectCompactTrigger}`
     *
     * With extra props: `trigger={(props) => <DropdownSelectLabeledTrigger label="Run Autopay On" {...props} />}`
     *
     * Defaults to DropdownSelectCompactTrigger.
     */
    trigger?: TriggerProp;
    /** Controlled selected value. Pass null to indicate controlled with no selection. */
    value?: T | null;
    /** Initial value for uncontrolled usage. */
    defaultValue?: T;
    onChange?: (value: T) => void;
    /** When true, the listbox width matches the trigger width instead of sizing to content. */
    matchTriggerWidth?: boolean;
}

export const DropdownSelect = <T extends string | number>({
    children,
    trigger = DropdownSelectCompactTrigger,
    value,
    defaultValue,
    onChange,
    matchTriggerWidth = false
}: DropdownSelectProps<T>) => {
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState<T | undefined>(defaultValue);
    const [internalOpen, setInternalOpen] = useState(false);
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
    const [shouldRender, setShouldRender] = useState(false);
    const triggerRef = useRef<HTMLElement | null>(null);
    const listboxId = useId();

    const selectedValue = isControlled ? (value ?? undefined) : internalValue;
    const isOpen = internalOpen;
    const pendingFrameHandle = useRef<number | null>(null);
    const pendingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const setIsOpen = (value: boolean) => {
        // Cancel any in-flight async work so rapid successive opens/closes
        // don't clobber state
        if (pendingFrameHandle.current !== null) {
            cancelAnimationFrame(pendingFrameHandle.current);
            pendingFrameHandle.current = null;
        }
        if (pendingTimeout.current !== null) {
            clearTimeout(pendingTimeout.current);
            pendingTimeout.current = null;
        }

        if (value) {
            // First render the element (with opacity 0)
            setShouldRender(true);
            // Wait for next frame before triggering the fade in
            pendingFrameHandle.current = requestAnimationFrame(() => {
                pendingFrameHandle.current = null;
                setInternalOpen(true);
            });
        } else {
            // Immediately hide (fade out)
            setInternalOpen(false);
            // Delay unmounting to allow fade out animation
            pendingTimeout.current = setTimeout(() => {
                pendingTimeout.current = null;
                setShouldRender(false);
            }, 200);
        }
    };

    const onSelect = (val: T) => {
        if (!isControlled) {
            setInternalValue(val);
        }
        onChange?.(val);
    };

    // Configure Popper
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: `bottom`,
        modifiers: [
            {
                name: `offset`,
                options: {
                    offset: [0, 4]
                }
            },
            {
                name: `preventOverflow`,
                options: {
                    padding: 8
                }
            },
            {
                name: `flip`,
                options: {
                    fallbackPlacements: [`top`, `bottom`, `left`, `right`]
                }
            }
        ]
    });

    // Handle clicks outside both trigger and listbox
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const clickedOutsideTrigger = referenceElement && !referenceElement.contains(target);
            const clickedOutsideListbox = popperElement && !popperElement.contains(target);

            if (clickedOutsideTrigger && clickedOutsideListbox) {
                setIsOpen(false);
            }
        };

        document.addEventListener(`mousedown`, handleClickOutside);
        return () => {
            document.removeEventListener(`mousedown`, handleClickOutside);
        };
    }, [isOpen, referenceElement, popperElement]);

    const listboxRefCallback = (node: HTMLDivElement | null) => {
        setPopperElement(node);
        if (node && isOpen) {
            // Focus selected option, or first option if nothing is selected
            const options = Array.from(node.querySelectorAll(`[role="option"]`)) as HTMLElement[];
            const selected = options.find(o => o.getAttribute(`aria-selected`) === `true`);
            // preventScroll should prevent the page from scrolling to the top
            (selected ?? options[0])?.focus({ preventScroll: true });
        }
    };

    const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
        triggerRef.current = event.currentTarget;
        setReferenceElement(event.currentTarget);
        setIsOpen(!isOpen);
    };

    const closeSelect = () => {
        setIsOpen(false);
        triggerRef.current?.focus();
    };

    const handleListboxKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const listboxElement = event.currentTarget;
        const options = Array.from(
            listboxElement.querySelectorAll(`[role="option"]`)
        ) as HTMLElement[];
        const currentIndex = options.indexOf(document.activeElement as HTMLElement);

        switch (event.key) {
            case `ArrowDown`: {
                event.preventDefault();
                const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
                options[nextIndex]?.focus();
                break;
            }
            case `ArrowUp`: {
                event.preventDefault();
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
                options[prevIndex]?.focus();
                break;
            }
            case `Home`:
                event.preventDefault();
                options[0]?.focus();
                break;
            case `End`:
                event.preventDefault();
                options[options.length - 1]?.focus();
                break;
            case `Tab`:
                event.preventDefault();
                setIsOpen(false);
                if (triggerRef.current) {
                    focusNext(triggerRef.current, event.shiftKey);
                }
                break;
            case `Escape`:
                event.preventDefault();
                closeSelect();
                break;
        }
    };

    const contextValue: DropdownSelectContextValue = {
        selectedValue,
        onSelect: onSelect as (value: unknown) => void,
        closeSelect
    };

    // Build trigger element, injecting value/isOpen/onClick/aria-* as props.
    const displayLabel = getLabelForValue(children, selectedValue);
    const triggerElement = trigger({
        value: displayLabel,
        isOpen,
        onClick: handleTriggerClick,
        'aria-expanded': isOpen,
        'aria-haspopup': `listbox`,
        'aria-controls': listboxId
    });

    return (
        <DropdownSelectContext.Provider value={contextValue}>
            {triggerElement}
            {shouldRender && createPortal(
                <div
                    ref={listboxRefCallback}
                    style={{
                        ...styles.popper,
                        zIndex: 1000,
                        opacity: isOpen ? 1 : 0,
                        transition: `opacity 200ms ease-in-out`
                    }}
                    {...attributes.popper}
                >
                    <div
                        role="listbox"
                        id={listboxId}
                        onKeyDown={handleListboxKeyDown}
                        className={`flex max-h-[248px] overflow-y-auto p-2 flex-col items-start gap-2 rounded-lg border border-neutral-300 bg-white ${matchTriggerWidth ? `` : `w-fit max-w-[232px]`}`}
                        style={{
                            boxShadow: `0 2px 4px 0 rgba(34, 41, 51, 0.24)`,
                            ...(matchTriggerWidth && referenceElement ? { minWidth: referenceElement.offsetWidth } : {})
                        }}
                    >
                        {children}
                    </div>
                </div>,
                document.body
            )}
        </DropdownSelectContext.Provider>
    );
};
